import { useMemo, useState } from "react";
import { Button, Icon, Modal } from "@ttc3k/trekker";
import { NavArrowLeft } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { getImageDimension } from "shared/merchCatalog";
import { Box } from "styled-system/jsx";
import { useBookerContext, useCartContext } from "widgets/booker/contexts";
import { ModalContentLayout } from "widgets/booker/shared";
import { Checkout } from "../Checkout";
import { ContentHeader } from "../ContentHeader";
import { Steps } from "../Steps";
import { getCartVariantQuantities } from "../Steps/components/MerchVariantSelection";
import { CatalogCartEditingProvider } from "./catalogCartEditing";
import { STORE_FROZEN_BACKDROP_STYLE } from "./catalogPaneStyles";
import { MerchProductOverlay } from "./MerchProductOverlay";

type CatalogCheckoutPaneProps = {
  open: boolean;
  onClose: () => void;
};

/** Checkout slides in over the store, which stays visible (dimmed) behind it. */
export const CatalogCheckoutPane = ({
  open,
  onClose,
}: CatalogCheckoutPaneProps) => {
  const { bookerPosition, products } = useBookerContext();
  const { cart } = useCartContext();
  const { t } = useTranslation("booker", { keyPrefix: "MERCH_CATALOG" });

  const [details, setDetails] = useState<{
    productId: string;
    variantId: string;
  } | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const cartVariantQuantities = useMemo(
    () => getCartVariantQuantities(cart),
    [cart],
  );

  const editing = useMemo(
    () => ({
      openItemDetails: (productId: string, variantId: string) => {
        setDetails({ productId, variantId });
        setDetailsOpen(true);
      },
      // Removing the last item from the summary leaves nothing to check out.
      onCartEmptied: onClose,
    }),
    [onClose],
  );

  const detailsProduct =
    products.find(p => p._id === details?.productId) ?? null;
  const imageDimValueIds = new Set(
    (getImageDimension(detailsProduct)?.values ?? []).map(v => v?._id),
  );
  const detailsValueId =
    detailsProduct?.merchMetadata?.variants
      ?.find(v => v?._id === details?.variantId)
      ?.dimensionValueIds?.find(id => imageDimValueIds.has(id)) ?? null;

  return (
    <Modal
      open={open}
      onOpenChange={({ open: next }: { open: boolean }) => {
        if (!next) onClose();
      }}
      placement={bookerPosition === "left" ? "leftPane" : "rightPane"}
      backdropProps={{ style: STORE_FROZEN_BACKDROP_STYLE }}
      hideCloseTrigger
      portal
    >
      <CatalogCartEditingProvider value={editing}>
        <ModalContentLayout
          imagesExist={false}
          header={<ContentHeader imagesExist={false} secureCheckout />}
        >
          <Box>
            <Button mode="light" type="button" onClick={onClose}>
              <Icon Element={NavArrowLeft} size="sm" />
              {t("BACK_TO_STORE")}
            </Button>
          </Box>
          <Steps />
          <Checkout />
        </ModalContentLayout>
        <MerchProductOverlay
          product={detailsProduct}
          open={detailsOpen}
          initialValueId={detailsValueId}
          onClose={() => setDetailsOpen(false)}
          cartVariantQuantities={cartVariantQuantities}
        />
      </CatalogCartEditingProvider>
    </Modal>
  );
};
